import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { Heading, Text } from "@/components/Typography";
import { api, SuggestedAdvisor } from "@/services/api";
import { useLocation, useNavigate } from "react-router-dom";
import { useErrorHandler } from "@/context/ErrorHandlerContext";
import { AIMatchingLoadingPage } from "./AIMatchingLoadingPage";
import { SendInvitationsModal } from "./components/SendInvitationsModal";
import { InviteToPabModal } from "./components/InviteToPabModal";
import { SuggestedAdvisorCard } from "./components/SuggestedAdvisorCard";
import { NormalButton } from "@/components/NormalButton";
import { ViewAdvisorProfileDrawer } from "./components/ViewAdvisorProfileDrawer";
import { useToast } from "@/hooks/useToast";
import {
  MAX_ADVISORS_REACHED,
  SESSION_NOT_FOUND,
} from "@/utils/toastMessages";
import { advisorProfileViewed, matchingComplete, pabInvitationSent, profileStepCompleted } from "@/utils/tracking";
import { initSessionReplay } from "@/utils/amplitude";
import { useAuth } from "@/hooks/useAuth";
import { getPrimaryRole, hasRole } from "@/utils/roleUtils";
import { useUpdateOnboardingStep } from "@/hooks/useUpdateOnboardingStep";
import { useAdvisorMatching } from "@/hooks/useAdvisorMatching";
import { cn } from "@/utils/cn";
import { AxiosError } from "axios";
import { AdvisorMatchingHeader } from "./components/AdvisorMatchingHeader";
import { PabInvitationsProgress } from "./components/PabInvitationsProgress";
import { BuildPabFooter } from "@/pages/onboarding/build-pab/components/BuildPabFooter";
import { TOTAL_BUILD_PAB_STEPS } from "@/pages/home/components/get-started/buildPabSteps";
import { Sparkles, UserRoundPlus, Home, ArrowRight, Mail, CircleCheck } from "lucide-react";
import { SecondaryButton } from "../../../components/buttonVariants";

const MIN_ADVISORS = 3;
const MAX_ADVISORS = 3;
/**
 * Invitation statuses that still hold a seat on the board. An advisor who has
 * ACCEPTED is no longer "invited", so both must count — otherwise progress
 * regresses as invites are accepted. rejected/withdrawn/expired free the seat.
 */
const ACTIVE_INVITE_STATUSES = ["invited", "accepted"];
/**
 * Deferred-invite ("Added To Advisory Board") encoding.
 *
 * When the achiever still owes payment, advisors are persisted but NOT
 * emailed — the backend stores them as `suggested` with `isSelected` true, a
 * pair that is otherwise unreachable. Those advisors hold a seat exactly like
 * an invited one, so they must count toward MIN_ADVISORS; without that a
 * paying user could never satisfy the 3-advisor requirement and would never
 * reach the payment step.
 */
const STAGED_INVITE_STATUS = "suggested";
const SEAT_HOLDING_STATUSES = [...ACTIVE_INVITE_STATUSES, STAGED_INVITE_STATUS];
const INITIAL_DISPLAY_CARD_COUNT = 5;
const MATCH_REQUEST_BATCH_SIZE = 5;
const REACH_OUT_THRESHOLD_COUNT = 100;

interface SelectAdvisorsPageProps {
  /**
   * When true, this page is rendered inside the Build-PAB wizard's own chrome
   * (BuildPabHeader/BuildPabFooter) — hide this page's own fixed bottom bar and
   * let the wizard's footer drive continuing, via onContinue/canContinue.
   */
  embedded?: boolean;
  /** Called instead of navigating to /home when the achiever finishes (embedded mode only). */
  onContinue?: () => void;
  /** Wizard Back (embedded mode only) — returns to the previous wizard step. */
  onBack?: () => void;
}

export const SelectAdvisorsPage = ({
  embedded = false,
  onContinue,
  onBack,
}: SelectAdvisorsPageProps = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: isUserLoading, refreshUser } = useAuth();
  const userRole = user ? getPrimaryRole(user) : "unknown";
  const { pabSessionId: stateSessionId } = location.state || {};
  const [selected, setSelected] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAdvisorForProfile, setSelectedAdvisorForProfile] =
    useState<SuggestedAdvisor | null>(null);
  const [selectedAdvisorForInvite, setSelectedAdvisorForInvite] = useState<SuggestedAdvisor | null>(
    null,
  );
  const [inviteSummaryDraft, setInviteSummaryDraft] = useState<string | null>(null);
  const [isInviteSummaryLoading, setIsInviteSummaryLoading] = useState(false);
  const [inviteSummaryCache, setInviteSummaryCache] = useState<Record<string, string>>({});
  const inviteSummaryRequestRef = useRef(0);
  const handleApiError = useErrorHandler();
  const { showToast } = useToast();
  const { updateStep } = useUpdateOnboardingStep();
  const queryClient = useQueryClient();
  const [displayCardCount, setDisplayCardCount] = useState(INITIAL_DISPLAY_CARD_COUNT);
  const [error, setError] = useState<string | null>(null);
  const [showMatchingLoader, setShowMatchingLoader] = useState(true);
  const [isFinishingLoader, setIsFinishingLoader] = useState(false);

  useEffect(() => {
    initSessionReplay();
  }, []);

  // Use React Query to fetch PAB session.
  // Embedded (inside the Build-PAB wizard): getActiveSession() only returns sessions
  // with status ACTIVE/COMPLETED — a session created earlier in the wizard can
  // legitimately not match that filter yet, resolving to null with no error. Falling
  // back to initialize() (get-or-create, same pattern as BuildPabStep4GoalsSetup.tsx)
  // ensures we still land on the achiever's real in-progress session instead of
  // treating "no session" as "user never started" and bouncing them away.
  const {
    data: session,
    isLoading: sessionLoading,
    isError: sessionError,
    error: sessionErrorData,
  } = useQuery({
    queryKey: ["pab-session", "active", embedded],
    queryFn: async () => {
      const activeSession = await api.pabSessions.getActiveSession();
      if (activeSession || !embedded) return activeSession;
      return api.pabSessions.initialize();
    },
    retry: 1,
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: !stateSessionId && hasRole(user, "achiever"), // Only fetch if no stateSessionId and user is achiever
  });

  const { data: achieverMe } = useQuery({
    queryKey: ["achiever", "me"],
    queryFn: api.achievers.getAchieverMe,
    staleTime: 60 * 60 * 1000,
    enabled: hasRole(user, "achiever"),
  });

  // Determine the session ID to use: prefer stateSessionId, fallback to session from query
  const pabSessionId = stateSessionId || session?.id || null;
  const achieverId = session?.achieverUserId || achieverMe?.id || null;

  const {
    data,
    isLoading: advisorsLoading,
    isError: advisorsError,
    error: advisorsErrorData,
  } = useAdvisorMatching(pabSessionId, Boolean(pabSessionId));

  // Fetch already-invited advisors for this session to restore state on refresh
  const { data: selectedAdvisorsData, isLoading: selectedAdvisorsLoading } = useQuery({
    queryKey: ["selected-advisors", pabSessionId],
    queryFn: () => api.pabSessions.getSelectedAdvisors(pabSessionId!),
    enabled: Boolean(pabSessionId),
    staleTime: 0,
  });

  const advisors = data?.items || [];
  // isUserLoading guards against the moment before AuthContext resolves `user` — until
  // then, hasRole(user, "achiever") is false, which disables every query below and makes
  // them all report isLoading=false, flashing an empty results screen before the loader.
  //
  // !pabSessionId guards a second, similar gap: on the render where `session` first
  // resolves and pabSessionId goes from null -> a real id, useAdvisorMatching/selected-
  // advisors flip from disabled to enabled in the SAME render — but React Query doesn't
  // synchronously report isLoading=true for a query that just became enabled this tick,
  // so advisorsLoading/selectedAdvisorsLoading can both still read false for that one
  // frame. Without this guard, `loading` would flip false→true→false and permanently
  // hide the AI-matching loader before the advisor-matching request ever fires.
  const loading =
    isUserLoading ||
    sessionLoading ||
    !pabSessionId ||
    advisorsLoading ||
    selectedAdvisorsLoading;
  // Counts staged advisors too — see SEAT_HOLDING_STATUSES. Server-derived, so
  // it is a trustworthy gate for "can this achiever finish".
  const invitedCount =
    selectedAdvisorsData?.filter((a) => SEAT_HOLDING_STATUSES.includes(a.approvalStatus)).length ??
    0;
  const selectedProgress = Math.min(invitedCount, MIN_ADVISORS);

  // Initialise `selected` from the already-invited advisors list once both data sets load.
  // Only advisors still holding a seat are seeded — otherwise a rejected or
  // withdrawn advisor keeps occupying one of the MAX_ADVISORS slots, and the
  // achiever can't select a replacement ("maximum reached" fires immediately).
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!selectedAdvisorsData) return;
    const alreadyInvitedIds = selectedAdvisorsData
      .filter((a) => SEAT_HOLDING_STATUSES.includes(a.approvalStatus))
      .map((a) => a.userId);
    setSelected(alreadyInvitedIds);
    initializedRef.current = true;
  }, [selectedAdvisorsData]);

  // Handle all errors from both session and advisor matching queries
  useEffect(() => {
    let errorMessage: string | null = null;

    // Check session error first
    if (sessionError && sessionErrorData) {
      const sessionAxiosError = sessionErrorData as AxiosError<{ detail?: string }>;
      errorMessage =
        sessionAxiosError.response?.data?.detail ||
        sessionAxiosError.message ||
        "Failed to load session. Please try again.";
    }

    // Check advisor matching error (only if no session error)
    if (!errorMessage && advisorsError && advisorsErrorData) {
      const advisorsAxiosError = advisorsErrorData as AxiosError<{ detail?: string }>;
      errorMessage =
        advisorsAxiosError.response?.data?.detail ||
        advisorsAxiosError.message ||
        "Failed to load experts. Please try again.";
    }

    // Set error if found
    setError(errorMessage);

    // Show toast notification if error
    if (errorMessage) {
      showToast({
        type: "error",
        message: errorMessage,
        duration: 5000,
      });
    }
  }, [sessionError, sessionErrorData, advisorsError, advisorsErrorData, showToast]);

  useEffect(() => {
    // Covers two cases once the session query has settled with no usable session:
    // (1) it errored (e.g. 404), or (2) it resolved cleanly to null/undefined — which
    // getActiveSession() does for "no active PAB" with no exception thrown. Without
    // case (2), a genuinely-missing session would leave pabSessionId null forever and
    // (combined with the !pabSessionId loading guard above) spin the loader forever.
    // Embedded (Build-PAB wizard) always falls back to initialize() above, so this
    // should only ever apply to the standalone /onboarding/advisor-matching route.
    if (!embedded && !stateSessionId && !sessionLoading && !session) {
      const sessionAxiosError = sessionErrorData as AxiosError<{ detail?: string }> | null;
      if (!sessionError || sessionAxiosError?.response?.status === 404) {
        navigate("/onboarding/goal-setting", { replace: true });
      }
    }
  }, [
    embedded,
    stateSessionId,
    sessionError,
    sessionErrorData,
    sessionLoading,
    session,
    navigate,
  ]);

  // Track matching complete when data is loaded
  useEffect(() => {
    if (data?.items && user?.id && pabSessionId) {
      const topMatchAdvisor = data.items.find((advisor) => advisor.topMatch);
      matchingComplete({
        pab_session_id: pabSessionId,
        achiever_user_id: user.id,
        advisors_count: data.items.length,
        top_match_advisor_id: topMatchAdvisor?.id || null,
      });
    }
  }, [data, user, pabSessionId]);

  const buildAdvisorIdsForSummary = (targetAdvisorId: string): string[] => {
    // v3: advisor ids are user_id strings (SHA256) — keep them as strings.
    // (Previously map(Number) turned them into NaN.)
    const uniqueAdvisorIds = Array.from(new Set([...selected, targetAdvisorId]));

    if (uniqueAdvisorIds.length < MIN_ADVISORS) {
      const additionalAdvisorIds = advisors
        .map((advisor) => advisor.id)
        .filter((advisorId) => !uniqueAdvisorIds.includes(advisorId))
        .slice(0, MIN_ADVISORS - uniqueAdvisorIds.length);
      uniqueAdvisorIds.push(...additionalAdvisorIds);
    }

    return uniqueAdvisorIds.slice(0, MAX_ADVISORS);
  };

  const loadInviteSummaryDraft = async (advisor: SuggestedAdvisor) => {
    const cachedDraft = inviteSummaryCache[advisor.id];
    if (cachedDraft) {
      setInviteSummaryDraft(cachedDraft);
      setIsInviteSummaryLoading(false);
      return;
    }

    if (!achieverId || !pabSessionId) {
      setInviteSummaryDraft(null);
      return;
    }

    const advisorIds = buildAdvisorIdsForSummary(advisor.id);
    if (advisorIds.length < MIN_ADVISORS) {
      setInviteSummaryDraft(null);
      return;
    }

    const requestId = inviteSummaryRequestRef.current + 1;
    inviteSummaryRequestRef.current = requestId;
    setIsInviteSummaryLoading(true);

    try {
      const response = await api.achievers.generateAdvisorInviteSummaries(achieverId, {
        sessionId: pabSessionId,
        advisorUserIds: advisorIds,
      });

      if (inviteSummaryRequestRef.current !== requestId) {
        return;
      }

      const matchingDraft = response.summaries.find(
        (summary) => String(summary.advisorUserId) === advisor.id,
      )?.summaryDraft;

      if (matchingDraft) {
        setInviteSummaryDraft(matchingDraft);
        setInviteSummaryCache((prev) => ({ ...prev, [advisor.id]: matchingDraft }));
      } else {
        setInviteSummaryDraft(null);
      }
    } catch {
      if (inviteSummaryRequestRef.current === requestId) {
        setInviteSummaryDraft(null);
      }
    } finally {
      if (inviteSummaryRequestRef.current === requestId) {
        setIsInviteSummaryLoading(false);
      }
    }
  };

  const handleSelectAdvisor = (advisorId: string) => {
    if (selected.includes(advisorId)) {
      setSelected((prev) => prev.filter((id) => id !== advisorId));
      return;
    }

    if (selected.length >= MAX_ADVISORS) {
      showToast({
        type: "info",
        message: MAX_ADVISORS_REACHED(MAX_ADVISORS),
        duration: 3000,
      });
      return;
    }

    setSelected((prev) => [...prev, advisorId]);
  };

  const handleInviteButtonClick = (advisor: SuggestedAdvisor) => {
    if (selected.includes(advisor.id)) {
      handleSelectAdvisor(advisor.id);
      return;
    }

    if (selected.length >= MAX_ADVISORS) {
      showToast({
        type: "info",
        message: MAX_ADVISORS_REACHED(MAX_ADVISORS),
        duration: 3000,
      });
      return;
    }

    const cachedDraft = inviteSummaryCache[advisor.id] ?? null;
    setSelectedAdvisorForInvite(advisor);
    setInviteSummaryDraft(cachedDraft);
    setIsInviteSummaryLoading(!cachedDraft);
    setInviteModalOpen(true);
    void loadInviteSummaryDraft(advisor);
  };

  const handleCloseInviteModal = () => {
    inviteSummaryRequestRef.current += 1;
    setIsInviteSummaryLoading(false);
    setInviteSummaryDraft(null);
    setInviteModalOpen(false);
    setSelectedAdvisorForInvite(null);
  };

  const handleConfirmInviteToPab = async (advisor: SuggestedAdvisor, message: string) => {
    if (!pabSessionId) {
      showToast({
        type: "error",
        message: SESSION_NOT_FOUND,
        duration: 3000,
      });
      return;
    }

    setConfirming(true);
    try {
      // v3: id is the advisor's user_id (SHA256 string) -- Number(advisor.id)
      // used to send NaN to the backend, silently breaking single invite send.
      const result = await api.pabSessions.sendInvitations(pabSessionId, [
        { id: advisor.id, inviteMessage: message },
      ]);

      if (user?.id) {
        pabInvitationSent({
          pab_session_id: pabSessionId,
          achiever_user_id: user.id,
          advisor_id: advisor.id,
          match_score: advisor.matchScore,
        });
      }

      // The server decides whether this was sent or merely staged (it depends
      // on entitlement, which the client must not evaluate itself). Say which
      // happened, so the achiever isn't left thinking an email went out when
      // it hasn't — that expectation gap is how people end up chasing advisors
      // who were never contacted.
      if (result?.staged) {
        showToast({
          type: "success",
          message: `${advisor.name || "Advisor"} added to your board. They'll be invited once your payment is complete.`,
          duration: 4000,
        });
      }

      handleSelectAdvisor(advisor.id);
      void queryClient.invalidateQueries({ queryKey: ["selected-advisors", pabSessionId] });
      handleCloseInviteModal();
    } catch (err) {
      handleApiError(err);
    } finally {
      setConfirming(false);
    }
  };

  const handleSendInvites = async () => {
    if (!pabSessionId) {
      showToast({
        type: "error",
        message: SESSION_NOT_FOUND,
        duration: 3000,
      });
      return;
    }

    setConfirming(true);
    try {
      // v3: id is the advisor's user_id (SHA256 string) -- Number(id) here
      // used to send NaN to the backend, silently breaking every invite send.
      const advisorIds = selected.map((id) => {
        return { id, inviteMessage: inviteSummaryCache[id] ?? "" };
      });
      await api.pabSessions.sendInvitations(pabSessionId, advisorIds);

      // Track pab_invitation_sent for each selected advisor
      if (user?.id) {
        selected.forEach((advisorId) => {
          const advisor = advisors.find((a) => a.id === advisorId);
          if (advisor) {
            pabInvitationSent({
              pab_session_id: pabSessionId,
              achiever_user_id: user.id,
              advisor_id: advisor.id,
              match_score: advisor.matchScore,
            });
          }
        });
      }

      setModalOpen(false);

      // Update onboarding step to complete (achievers finish after advisor matching)
      await updateStep("complete");

      // Navigate to next step or dashboard
      navigate("/home");
    } catch (err) {
      handleApiError(err);
    } finally {
      setConfirming(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const getSelectedAdvisors = () => {
    return advisors.filter((advisor) => selected.includes(advisor.id));
  };

  const handleViewProfile = (advisor: SuggestedAdvisor) => {
    if (user?.id) {
      advisorProfileViewed({
        advisor_user_id: advisor.id,
        viewer_user_id: user.id,
        source: "matching",
      });
    }
    setSelectedAdvisorForProfile(advisor);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedAdvisorForProfile(null);
  };

  const handleDrawerInviteToPab = () => {
    if (!selectedAdvisorForProfile) return;
    const advisor = selectedAdvisorForProfile;
    handleCloseDrawer();
    handleInviteButtonClick(advisor);
  };

  const canFinish = invitedCount >= MIN_ADVISORS;

  const handleFinish = () => {
    if (!canFinish) return;
    profileStepCompleted({ step_name: "explore_homepage_clicked", user_role: userRole });
    void updateStep("complete")
      .then(async () => {
        // Inside the Build-PAB wizard (onContinue), the WIZARD decides what
        // comes next: the payment step for users who owe it -- which saves
        // step 6 -- or finishing, which saves "finished". Writing "finished"
        // here, before payment, hid every way back to the payment step from
        // Home for anyone who left it unpaid.
        //
        // Standalone advisor matching has no wizard after it, so it marks the
        // wizard finished itself; a board still waiting on payment resumes at
        // the payment step from Home regardless (see buildPabResumeStep).
        if (!onContinue && user?.buildPabStep) {
          await api.users.updateBuildPabStep(TOTAL_BUILD_PAB_STEPS + 1);
          await refreshUser();
        }
      })
      .then(() => (onContinue ? onContinue() : navigate("/home")));
  };

  const matchingViewedTrackedRef = useRef(false);

  useEffect(() => {
    if (loading) {
      setShowMatchingLoader(true);
      setIsFinishingLoader(false);
      return;
    }
    if (showMatchingLoader) {
      setIsFinishingLoader(true);
    }
  }, [loading, showMatchingLoader]);

  useEffect(() => {
    if (!showMatchingLoader && !matchingViewedTrackedRef.current) {
      matchingViewedTrackedRef.current = true;
      profileStepCompleted({ step_name: "advisor_matching_viewed", user_role: userRole });
    }
  }, [showMatchingLoader, userRole]);

  // Full-page error state.
  //
  // Deliberately NOT shown for an advisor-matching failure, and never inside
  // the Build-PAB wizard.
  //
  // It used to render for ANY error, with the heading "Session Not Found" and a
  // "Go to Goal Setting" button. Two things went wrong with that:
  //
  //   1. A failure to load EXPERTS (matching timed out, LLM hiccup, empty pool)
  //      is not a missing session -- but it hit this branch and told the user
  //      their session didn't exist, then offered to send them back to goal
  //      setting, which fixes nothing and loses their place.
  //   2. `embedded` is the Build-PAB wizard's step 5. Replacing the whole step
  //      with this screen dropped the achiever out of the wizard entirely --
  //      including out of the payment step that follows it.
  //
  // So this now covers only the case it was written for: a genuinely absent
  // session on the standalone route. Everything else is handled inline below,
  // where the user keeps their context and can retry.
  if (error && sessionError && !embedded) {
    return (
      <div className="flex h-full flex-col font-inter bg-surface-page-bg">
        <div className="flex flex-1 items-center justify-center">
          <div className="max-w-md px-4 text-center">
            <Heading as="h2" size="3" className="mb-4 text-theme-text-heading">
              Session Not Found
            </Heading>
            <Text as="p" size="3" className="mb-6 text-theme-text-muted">
              {error}
            </Text>
            <Button variant="primary" onClick={() => navigate("/onboarding/goal-setting")}>
              Go to Goal Setting
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (showMatchingLoader) {
    return (
      <div className="font-inter bg-surface-page-bg">
        <AIMatchingLoadingPage
          message="Our AI is analyzing your goals and finding the perfect experts for your journey..."
          isCompleting={isFinishingLoader}
          onComplete={() => {
            setShowMatchingLoader(false);
            setIsFinishingLoader(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className={cn("min-h-full bg-surface-page-bg font-inter", !embedded && "pb-40")}>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        <AdvisorMatchingHeader
          description={`We've carefully selected experts based on your goals, experience, and preferences. Send Pab invitations to ${MIN_ADVISORS} experts to schedule introductory sessions.`}
        />

        {embedded && (
          <div className="mb-6 rounded-2xl border border-primary-350 bg-primary-250 px-5 py-4 dark:border-pab-dark-pill-border dark:bg-surface-card-inner sm:px-6">
            <PabInvitationsProgress
              count={selectedProgress}
              total={MIN_ADVISORS}
              description="Send at least 3 invitations to start your journey"
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2">
          {advisors.slice(0, displayCardCount).map((advisor) => {
            const approvalStatus = selectedAdvisorsData?.find(
              (a) => a.userId === advisor.id,
            )?.approvalStatus;

            return (
              <SuggestedAdvisorCard
                key={advisor.id}
                advisor={advisor}
                className="card advisor-card"
                actions={
                  <>
                    <SecondaryButton
                      borderColor="border-secondary-300"
                      bgColor=""
                      textColor="text-theme-text-muted"
                      hoverBgColor="hover:bg-secondary-50"
                      size="md"
                      className="advisor-invite-btn !h-10 rounded-[10px] font-medium"
                      onClick={() => handleViewProfile(advisor)}
                    >
                      View Profile
                    </SecondaryButton>

                    {approvalStatus === STAGED_INVITE_STATUS ? (
                      /* Deferred invite: recorded on the board, but the advisor
                         has NOT been contacted. They are emailed only once
                         payment clears. Figma node 184:7625. */
                      <NormalButton
                        size="md"
                        borderColor="border-transparent"
                        bgColor=""
                        textColor="text-white"
                        hoverBgColor=""
                        leftIcon={<UserRoundPlus className="h-4 w-4 text-white" />}
                        title="Added to your board — they'll be invited once payment is complete"
                        className="advisor-invite-btn flex-1 rounded-[10px] border-0 bg-gradient-to-r from-[#00c950] to-[#009966] font-medium tracking-[-0.3px] text-white"
                      >
                        Added To Advisory Board
                      </NormalButton>
                    ) : approvalStatus === "invited" ? (
                      <NormalButton
                        size="md"
                        borderColor="border-status-success-border"
                        bgColor="bg-status-success-bg"
                        textColor="text-status-success-text"
                        hoverBgColor=""
                        leftIcon={<CircleCheck className="h-4 w-4 text-status-success-text" />}
                        className="advisor-invite-btn flex-1 rounded-[10px] font-medium"
                      >
                        Invited
                      </NormalButton>
                    ) : (
                      <Button
                        variant="primaryGradient"
                        size="md"
                        onClick={() => handleInviteButtonClick(advisor)}
                        disabled={selected.length >= MAX_ADVISORS}
                        leftIcon={<UserRoundPlus className="h-4 w-4" />}
                        className="advisor-invite-btn border-0 text-white"
                      >
                        Invite To Advisory Board
                      </Button>
                    )}
                  </>
                }
              />
            );
          })}

          {advisors.length > displayCardCount && (
            <button
              type="button"
              className="flex min-h-60 items-center justify-center gap-2 rounded-2xl border border-dashed border-primary-350 bg-transparent p-6 text-secondary-purple transition-colors hover:bg-transparent sm:min-h-advisor-request-card"
              onClick={() =>
                setDisplayCardCount((prev) =>
                  Math.min(prev + MATCH_REQUEST_BATCH_SIZE, advisors.length),
                )
              }
            >
              <Sparkles className="h-5 w-5" />
              <Text as="span" className="text-20 font-medium text-secondary-purple">
                Request 5 More Matches
              </Text>
            </button>
          )}

          {advisors.length >= REACH_OUT_THRESHOLD_COUNT && advisors.length <= displayCardCount && (
            <Card className="flex min-h-[310px] flex-col items-center justify-center gap-4 p-6 text-center">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-primary-gradient">
                <Mail className="h-[22px] w-[22px] text-white" />
              </div>
              <Heading as="h3" className="text-18 font-bold text-theme-text-heading">
                Reach Out to Us for Better Matches
              </Heading>
              <Text
                as="p"
                className="max-w-[341px] text-14 leading-[22px] text-ai-goal-back-btn-text"
              >
                We&apos;ve shown you our top 15 matches. Need experts with different expertise or
                experience? Our team can help find the perfect fit.
              </Text>
              <Button
                variant="ghost"
                size="md"
                className="border border-secondary-300 text-secondary-700"
                leftIcon={<Mail className="h-[15px] w-[15px]" />}
                onClick={() => {
                  window.location.href = "mailto:help@forwardshare.co";
                }}
              >
                Contact Support
              </Button>
            </Card>
          )}
        </div>
      </div>

      {embedded ? (
        <BuildPabFooter
          onBack={onBack}
          onNext={handleFinish}
          nextDisabled={!canFinish}
          nextLabel="Continue"
        />
      ) : (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-surface-border bg-surface-card/95 backdrop-blur shadow-[0px_-4px_20px_0px_rgba(0,0,0,0.06)]">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-3 sm:px-8">
            {/* Left: PAB Invitations Info */}
            <PabInvitationsProgress
              count={selectedProgress}
              total={MIN_ADVISORS}
              description="Send at least 3 invitations to start your journey"
            />

            {/* Right: Explore Homepage button */}
            <Button
              variant={canFinish ? "primaryGradient" : "ghost"}
              size="md"
              className={cn(
                "flex h-[42px] shrink-0 items-center gap-1 whitespace-nowrap rounded-[14px] px-5 text-14 font-semibold",
                canFinish
                  ? "text-white"
                  : "border border-theme-border-subtle bg-theme-bg-subtle text-theme-text-muted",
              )}
              onClick={handleFinish}
              leftIcon={<Home className="h-[15px] w-[15px]" />}
              rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
            >
              Explore Homepage
            </Button>
          </div>
        </div>
      )}

      {/* Invite to PAB Modal */}
      <InviteToPabModal
        isOpen={inviteModalOpen}
        advisor={selectedAdvisorForInvite}
        generatedSummary={inviteSummaryDraft}
        isSummaryLoading={isInviteSummaryLoading}
        isLoading={confirming}
        onClose={handleCloseInviteModal}
        onConfirm={handleConfirmInviteToPab}
      />

      {/* Send Invitations Modal */}
      <SendInvitationsModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        selectedAdvisors={getSelectedAdvisors()}
        onSendInvites={handleSendInvites}
        isLoading={confirming}
      />

      {/* Advisor Profile Drawer */}
      <ViewAdvisorProfileDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        advisor={selectedAdvisorForProfile}
        onInviteToPab={handleDrawerInviteToPab}
        approvalStatus={
          selectedAdvisorsData?.find((a) => a.userId === selectedAdvisorForProfile?.id)
            ?.approvalStatus
        }
      />
    </div>
  );
};

export default SelectAdvisorsPage;
